import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/system";
import { colors, fontFamilies } from "~/theme";
import { ChainIconByChain } from "~/features/icon/ChainIconByChain";
import { IPFSOutlineIcon } from "~/features/icon/IPFSOutlineIcon";
import { MetadataOutlineIcon } from "~/features/icon/MetadataOutlineIcon";
import { ArtworkMedia } from "~/features/artwork";
import { ArtworkMintDate } from "./ArtworkMintDate";
import { ArtworkOwner } from "./ArtworkOwner";
import { ArtworkTokenDescription } from "./ArtworkTokenDescription";
import { ArtworkInfoViewProps } from "./ArtworkInfoViewProps";
import { ShareMenu } from "~/features/sharePage";
import ShareIcon from "~/features/icon/share.svg";
import { PhygitalInfo } from "~/features/PDPv2/info/PhygitalInfo";

const Container = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    "& H4": {
        display: "flex",
        alignItems: "center",
    },
    [theme.breakpoints.up(1100)]: {
        display: "grid",
        gridGap: 40,
        gridTemplateColumns: "auto minmax(min-content, 680px)",
        gridTemplateRows: "minmax(320px, max-content)",
    },
    [theme.breakpoints.down(768)]: {
        "& h1": {
            fontSize: "28px",
            lineHeight: "34px",
        },
    },
}));

const PageHeading = styled(Box)(() => ({
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: "15px",
}));

export const ArtworkInfoDesktopView: React.FC<ArtworkInfoViewProps> = ({
    tokenData,
    ownedByYou,
    etherscanUrl,
    handleOnChainIdClick,
    handleOnMetadataURIClick,
    handleOnMediaURIClick,
}) => {
    return (
        <Container data-target="artwork-info">
            <Box>
                <ArtworkMedia metadata={tokenData?.metadata} optimized />
            </Box>
            <Box data-target="artwork-info-info">
                <PageHeading>
                    <Box
                        data-testid="artwork-name"
                        fontFamily={fontFamilies.poppins}
                        fontStyle="normal"
                        fontWeight={400}
                        fontSize="40px"
                        letterSpacing="0.73px"
                    >
                        {tokenData?.metadata?.properties?.name?.description}
                    </Box>
                    <ShareMenu>
                        <ShareIcon />
                    </ShareMenu>
                </PageHeading>
                <Box display="flex" alignItems="center">
                    {tokenData?.metadata?.properties.artistName
                        ?.description && (
                        <Box
                            fontFamily={fontFamilies.poppins}
                            fontStyle="normal"
                            fontSize="16px"
                            fontWeight={400}
                            marginTop="16px"
                            marginRight="16px"
                            data-testid="artist-name"
                        >
                            Artist:{" "}
                            {
                                tokenData?.metadata?.properties.artistName
                                    ?.description
                            }
                        </Box>
                    )}
                    <ArtworkMintDate
                        data-testid="mint-date"
                        createdAt={
                            tokenData?.metadata?.properties?.createdAt
                                ?.description as string
                        }
                    />
                </Box>
                <Box
                    display="inline-flex"
                    marginTop="32px"
                    alignItems="center"
                    width="100%"
                >
                    <Box
                        minWidth="200px"
                        marginRight="16px"
                        data-testid="artwork-owner"
                    >
                        <Box
                            component="span"
                            fontFamily={fontFamilies.poppins}
                            fontStyle="normal"
                            fontSize="16px"
                            fontWeight={400}
                        >
                            Owner:
                        </Box>
                        <ArtworkOwner
                            ownedByYou={ownedByYou}
                            ownerWallet={tokenData.ownerOf as string}
                        />
                    </Box>
                </Box>
                <Box
                    marginTop="32px"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    padding="2px"
                    maxWidth="555px"
                    sx={(theme) => ({
                        [theme.breakpoints.down(767)]: {
                            minHeight: "110px",
                            justifyContent: "flex-start",
                        },
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            [theme.breakpoints.up(380)]: {
                                minWidth: "135px",
                            },
                        })}
                    >
                        <ChainIconByChain
                            href={`${etherscanUrl}/token/${tokenData.tokenAddress}?a=${tokenData.tokenId}`}
                            blockchainId={tokenData.blockchainId}
                            onClick={handleOnChainIdClick}
                            isNewPDP
                            width={17}
                        />
                    </Box>
                    {tokenData.tokenURI && (
                        <Box
                            sx={(theme) => ({
                                [theme.breakpoints.down(380)]: {
                                    minWidth: "190px !important",
                                },
                                [theme.breakpoints.down(767)]: {
                                    minWidth: "210px",
                                },
                            })}
                        >
                            <a
                                href={tokenData.tokenURI}
                                target="_blank"
                                rel="noreferrer"
                                onClick={handleOnMetadataURIClick}
                                data-testid="metadata-link"
                            >
                                <Box display="flex" alignItems="center">
                                    <Box
                                        marginRight="4px"
                                        display="flex"
                                        alignItems="center"
                                    >
                                        <MetadataOutlineIcon />
                                    </Box>
                                    <Box
                                        fontFamily={fontFamilies.poppins}
                                        fontWeight="normal"
                                        fontSize="15px"
                                        letterSpacing="0.4px"
                                        color={colors.black}
                                        sx={{
                                            textDecorationLine: "underline",
                                        }}
                                    >
                                        Metadata
                                    </Box>
                                </Box>
                            </a>
                        </Box>
                    )}
                    {tokenData.metadata?.properties?.media?.description && (
                        <Box data-testid="media">
                            <a
                                href={
                                    tokenData.metadata?.properties?.media
                                        ?.description as string
                                }
                                target="_blank"
                                rel="noreferrer"
                                onClick={handleOnMediaURIClick}
                                data-testid="media-link"
                            >
                                <Box display="flex" alignItems="center">
                                    <Box
                                        marginRight="4px"
                                        display="flex"
                                        alignItems="center"
                                    >
                                        <IPFSOutlineIcon />
                                    </Box>
                                    <Box
                                        fontFamily={fontFamilies.poppins}
                                        fontWeight="normal"
                                        fontSize="15px"
                                        letterSpacing="0.4px"
                                        color={colors.black}
                                        sx={{
                                            textDecorationLine: "underline",
                                        }}
                                    >
                                        IPFS
                                    </Box>
                                </Box>
                            </a>
                        </Box>
                    )}
                </Box>
                <ArtworkTokenDescription
                    artworkDescription={
                        tokenData.metadata?.properties?.description?.description
                    }
                />

                {tokenData.metadata?.phygitalData && (
                    <PhygitalInfo
                        phygitalData={tokenData.metadata?.phygitalData}
                    />
                )}
            </Box>
        </Container>
    );
};
